import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  substances: [],
  items: [
    {
      substance: '',
      substanceName: '',
      availableDosages: [],
      selectedDosage: '',
      dosage: '',
      dosageType: '',
      form: '',
      frequency: '',
      days: '',
    },
  ],
  loading: false,
  error: null,
};

const parseDosageString = (dosageStr) => {
  if (!dosageStr) return { dosage: '', dosageType: '', form: '' };

  const str = dosageStr.toLowerCase().trim();

  let form = 'Tablet';
  if (str.includes('capsule') || str.includes('iu')) {
    form = 'Capsule';
  } else if (
    str.includes('tablet') ||
    str.includes('druppels') ||
    str.includes('gram') ||
    str.includes('gr') ||
    str.includes('mg') ||
    str.includes('mcg')
  ) {
    form = 'Tablet';
  } else if (
    str.includes('olie') ||
    str.includes('liquid') ||
    str.includes('oil')
  ) {
    form = 'Liquid';
  } else if (str.includes('powder')) {
    form = 'Powder';
  }

  const numberMatch = str.match(/^(\d+[.,]?\d*)\s*([a-z]+)?/i);

  if (numberMatch) {
    const dosageValue = numberMatch[1];
    let type = numberMatch[2] || '';

    if (type === 'gram' || type === 'gr') {
      type = 'gram';
    } else if (type === 'mg') {
      type = 'mg';
    } else if (type === 'mcg') {
      type = 'mcg';
    } else if (type === 'capsule' || type === 'capsules') {
      type = 'capsule';
    } else if (type === 'tablet' || type === 'tablets') {
      type = 'tablet';
    }

    return {
      dosage: dosageValue,
      dosageType: type,
      form,
    };
  }

  return { dosage: '', dosageType: '', form };
};

const prescriptionSlice = createSlice({
  name: 'prescription',
  initialState,
  reducers: {
    setSubstances(state, action) {
      state.substances = action.payload;
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    addItem(state) {
      state.items.push({
        substance: '',
        substanceName: '',
        availableDosages: [],
        selectedDosage: '',
        dosage: '',
        dosageType: '',
        form: '',
        frequency: '',
        days: '',
      });
    },
    removeItem(state, action) {
      const index = action.payload;
      state.items = state.items.filter((_, i) => i !== index);
    },
    updateItemField(state, action) {
      const { index, field, value } = action.payload;
      if (state.items[index]) {
        state.items[index][field] = value;
      }
    },
    selectSubstance(state, action) {
      const { index, substanceName } = action.payload;
      const substance = state.substances.find(
        (s) => s.substance_name === substanceName
      );

      if (substance && state.items[index]) {
        const availableDosages = substance.available_dosage || [];
        const firstDosage = availableDosages[0] || '';
        const parsed = parseDosageString(firstDosage);

        let formValue = parsed.form || 'Tablet';
        if (!['Capsule', 'Tablet', 'Liquid', 'Powder'].includes(formValue)) {
          formValue = 'Tablet';
        }

        state.items[index] = {
          ...state.items[index],
          substance: substanceName,
          substanceName: substanceName,
          availableDosages,
          selectedDosage: firstDosage,
          dosage: parsed.dosage,
          dosageType: parsed.dosageType,
          form: formValue,
        };
      }
    },
    selectDosage(state, action) {
      const { index, dosageString } = action.payload;
      const parsed = parseDosageString(dosageString);

      if (state.items[index]) {
        let formValue = parsed.form || 'Tablet';
        if (!['Capsule', 'Tablet', 'Liquid', 'Powder'].includes(formValue)) {
          formValue = 'Tablet';
        }

        state.items[index].selectedDosage = dosageString;
        state.items[index].dosage = parsed.dosage;
        state.items[index].dosageType = parsed.dosageType;
        state.items[index].form = formValue;
      }
    },
    resetPrescription(state) {
      state.items = initialState.items;
    },
  },
});

export const {
  setSubstances,
  setLoading,
  setError,
  addItem,
  removeItem,
  updateItemField,
  selectSubstance,
  selectDosage,
  resetPrescription,
} = prescriptionSlice.actions;

export default prescriptionSlice.reducer;
